import { Capacitor } from '@capacitor/core';
import type { WaypointInput } from '../types/jobs';

type LocationCallback = (wp: WaypointInput) => void;

// Minimum spacing between accepted fixes. Tractors don't move fast — at 10
// km/h, 30 s is ~80 m of travel, which is plenty for a job's track and
// slashes the upload rate vs. the plugin's native ~1 Hz cadence.
export const DEFAULT_FIX_INTERVAL_MS = 30_000;

// A spray needs more. Its per-tank area counts ground within 15 m of a fix,
// so at 30 s (~60 m apart at 7 km/h) the gaps between fixes go uncounted —
// and the faster the tractor, the wider the gaps, so a fast tank reads as
// LESS ground covered and looks over-applied. 10 s is ~20 m apart, inside
// the 30 m a fix covers, at three times the upload rate.
export const SPRAY_FIX_INTERVAL_MS = 10_000;

/**
 * Native and browser APIs return values like `NaN`, `-1`, or `Infinity` for
 * fields the device can't produce (heading when stationary, altitude without a
 * barometer, etc.). The backend rejects these via Joi `number()` validation —
 * which used to cause every upload batch to 400 silently and points to stack
 * up in IndexedDB with a healthy-looking WiFi connection.
 */
function cleanNumber(v: number | null | undefined): number | null {
  if (v == null) return null;
  if (typeof v !== 'number') return null;
  if (!Number.isFinite(v)) return null;
  return v;
}

/**
 * Unified background location service.
 *
 * Native (Capacitor/Android): uses @capgo/background-geolocation with a
 * foreground service so GPS continues when the app is backgrounded.
 *
 * Web: falls back to navigator.geolocation.watchPosition for dev/test.
 */
class BackgroundLocationService {
  private callbacks: Set<LocationCallback> = new Set();
  private running = false;
  private webWatchId: number | null = null;
  private lastEmittedAt = 0;
  private minFixIntervalMs = DEFAULT_FIX_INTERVAL_MS;

  /**
   * Start tracking. Only one session at a time. The interval is set on every
   * start rather than remembered, so a job tracked after a spray is back at
   * the default without anyone having to reset it.
   */
  async start(minFixIntervalMs: number = DEFAULT_FIX_INTERVAL_MS): Promise<void> {
    if (this.running) return;
    this.running = true;
    this.lastEmittedAt = 0;
    this.minFixIntervalMs = minFixIntervalMs;

    if (Capacitor.isNativePlatform()) {
      await this.startNative();
    } else {
      this.startWeb();
    }
  }

  /** Stop tracking and clean up. */
  async stop(): Promise<void> {
    if (!this.running) return;
    this.running = false;

    if (Capacitor.isNativePlatform()) {
      await this.stopNative();
    } else {
      this.stopWeb();
    }
  }

  /**
   * Force-stop the platform GPS / foreground-service notification regardless
   * of this service's `running` flag.
   *
   * Why this exists: the Capgo foreground service runs in its own native
   * process. After a full app cold-start the JS-side `running` flag resets
   * to `false`, so a normal `stop()` short-circuits — but the OS-level
   * service from the previous session may still be alive, with its
   * notification pinned in the shade. Calling the plugin's `stop()`
   * unconditionally clears that orphan notification.
   *
   * Safe to call when nothing is running: the plugin treats it as a no-op.
   */
  async forceStopNative(): Promise<void> {
    this.running = false;
    if (Capacitor.isNativePlatform()) {
      try {
        await this.stopNative();
      } catch (e) {
        console.warn('[BackgroundLocation] forceStopNative failed', e);
      }
    } else {
      this.stopWeb();
    }
  }

  onLocation(cb: LocationCallback): void {
    this.callbacks.add(cb);
  }

  offLocation(cb: LocationCallback): void {
    this.callbacks.delete(cb);
  }

  isRunning(): boolean {
    return this.running;
  }

  // ---------------------------------------------------------------------------
  // Native (Capgo plugin)
  // ---------------------------------------------------------------------------

  private async startNative(): Promise<void> {
    const { BackgroundGeolocation } = await import('@capgo/background-geolocation');

    await BackgroundGeolocation.start(
      {
        backgroundTitle: 'FactreeTech GPS',
        backgroundMessage: 'Tracking in progress',
        distanceFilter: 10,
        requestPermissions: true,
        stale: false,
      },
      (location, error) => {
        if (error) {
          console.warn('[BackgroundLocation] error:', error);
          return;
        }
        if (!location) return;

        const wp: WaypointInput = {
          lat: location.latitude,
          lng: location.longitude,
          accuracy: cleanNumber(location.accuracy),
          altitude: cleanNumber(location.altitude),
          heading: cleanNumber(location.bearing),
          speed: cleanNumber(location.speed),
          recorded_at: location.time ? new Date(location.time).toISOString() : new Date().toISOString(),
        };
        this.emit(wp);
      },
    );
  }

  private async stopNative(): Promise<void> {
    const { BackgroundGeolocation } = await import('@capgo/background-geolocation');
    await BackgroundGeolocation.stop();
  }

  // ---------------------------------------------------------------------------
  // Web fallback (browser Geolocation API)
  // ---------------------------------------------------------------------------

  private startWeb(): void {
    if (!navigator.geolocation) {
      console.warn('[BackgroundLocation] Geolocation API not available');
      return;
    }

    this.webWatchId = navigator.geolocation.watchPosition(
      (pos) => {
        const wp: WaypointInput = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: cleanNumber(pos.coords.accuracy),
          altitude: cleanNumber(pos.coords.altitude),
          heading: cleanNumber(pos.coords.heading),
          speed: cleanNumber(pos.coords.speed),
          recorded_at: new Date().toISOString(),
        };
        this.emit(wp);
      },
      (err) => console.warn('[BackgroundLocation] web error:', err.message),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 5_000 },
    );
  }

  private stopWeb(): void {
    if (this.webWatchId !== null) {
      navigator.geolocation.clearWatch(this.webWatchId);
      this.webWatchId = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  private emit(wp: WaypointInput): void {
    const now = Date.now();
    if (now - this.lastEmittedAt < this.minFixIntervalMs) return;
    this.lastEmittedAt = now;
    for (const cb of this.callbacks) {
      try {
        cb(wp);
      } catch (e) {
        console.error('[BackgroundLocation] callback error:', e);
      }
    }
  }
}

const backgroundLocation = new BackgroundLocationService();
export default backgroundLocation;
